import { useTranslate } from "@similarweb/i18n-provider";
import { AlertBanner, OnOffSwitch, PixelPlaceholderLoader } from "@similarweb/ui-components";
import type { StackProps } from "@similarweb/ui-components/foundations";
import { Box, Flex, Stack } from "@similarweb/ui-components/foundations";

import { GaTriggerContainer } from "app/modules/ga-connect";

import { generateAutomationName } from "../../../../../helpers/generateAutomationName";
import { GaConnectButton, GaSettings, type IGaSettingsProps } from "../../../../ga-connect";
import { Section } from "../../styles";

import { SectionTitle } from "./SectionTitle";
import { GA_SECTION_ERROR_BANNER_TEXT, GA_SECTION_FAULTY_MAPPING_BANNER_TEXT } from "./texts";
import type { GaMappingSettings } from "./types";

const ContentStack = (props: StackProps) => (
    <Stack
        gap="12px"
        margin="0 0 24px 0"
        {...props}
    />
);

export interface IGaSectionContentProps extends Pick<IGaSettingsProps, "onPropertySelected" | "onStreamChange"> {
    gaMapping: GaMappingSettings;
    isLoading?: boolean;
    isMappingError?: boolean;
    isQueryError?: boolean;
    isViewerMode?: boolean;
    onSwitchClick?: () => void;
}

const GaSectionContent = ({
    gaMapping,
    isLoading,
    isMappingError,
    isQueryError,
    isViewerMode,
    onPropertySelected,
    onStreamChange,
    onSwitchClick,
}: IGaSectionContentProps) => {
    const { translate } = useTranslate();

    if (isLoading) {
        return (
            <ContentStack>
                <SectionTitle />
                <PixelPlaceholderLoader
                    width="500px"
                    height="40px"
                />
            </ContentStack>
        );
    }

    if (isQueryError) {
        return (
            <ContentStack gap="24px">
                <SectionTitle />
                <AlertBanner
                    variant="error"
                    data-automation={generateAutomationName("settings", ["section", "ga", "error-banner"])}
                >
                    <AlertBanner.Image />
                    <AlertBanner.Description>{translate(GA_SECTION_ERROR_BANNER_TEXT)}</AlertBanner.Description>
                </AlertBanner>
            </ContentStack>
        );
    }

    if (!gaMapping) {
        return (
            <ContentStack gap="24px">
                <SectionTitle />
                {isMappingError ? (
                    <AlertBanner
                        variant="info"
                        data-automation={generateAutomationName("settings", ["section", "ga", "info-banner-Faulty"])}
                    >
                        <AlertBanner.Image />
                        <AlertBanner.Description>
                            {translate(GA_SECTION_FAULTY_MAPPING_BANNER_TEXT)}
                        </AlertBanner.Description>
                    </AlertBanner>
                ) : null}
                {isViewerMode ? null : (
                    <GaTriggerContainer onPropertySelected={onPropertySelected}>
                        {({ open, isLoading: isGAConnectionsListLoading }) => (
                            <Box>
                                <GaConnectButton
                                    data-automation={generateAutomationName("settings", [
                                        "section",
                                        "ga",
                                        "connect-button",
                                    ])}
                                    isLoading={isGAConnectionsListLoading}
                                    onClick={open}
                                />
                            </Box>
                        )}
                    </GaTriggerContainer>
                )}
            </ContentStack>
        );
    }

    return (
        <ContentStack width="500px">
            <Flex>
                <SectionTitle />
                <OnOffSwitch
                    onClick={onSwitchClick}
                    isSelected={gaMapping.isActive}
                    isDisabled={isViewerMode}
                />
            </Flex>
            <GaSettings
                connectionId={gaMapping.connectionId}
                isDisabled={isViewerMode}
                onPropertySelected={onPropertySelected}
                onStreamChange={onStreamChange}
                propertyId={gaMapping.propertyId}
                propertyName={gaMapping.propertyName}
                streamId={gaMapping.streamId}
            />
        </ContentStack>
    );
};

export const GaSection = (props: IGaSectionContentProps) => {
    return (
        <Section
            data-automation={generateAutomationName("settings", ["section", "ga"])}
            className="noTopPadding"
            withSeparator
        >
            <GaSectionContent {...props} />
        </Section>
    );
};
